import { useState, useEffect, useMemo } from "react";

import { FiltersStateOnTable } from "types/filters";
import { FiltersToApplyOnCampaignsPage } from "containers/admin/clients/client/campaigns-page/components/filters-on-campaigns-table/filters-on-campaigns-table.types";

import {
  useGetProgramTypesQuery,
  useGetCampaignObjectiveTypesQuery,
  useGetAudienceTypesQuery,
} from "hooks/api/dictionary/dictionary";

import { CAMPAIGNS_TABLE_FILTERS_DATA } from "containers/admin/clients/client/campaigns-page/components/filters-on-campaigns-table/filters-on-campaigns-table.consts";

type UseFiltersOnCampaignsTableParams = FiltersStateOnTable<FiltersToApplyOnCampaignsPage>;

export const useFiltersOnCampaignsTable = ({
  filtersToApply,
  setFiltersToApply,
}: UseFiltersOnCampaignsTableParams) => {
  const [currentFilters, setCurrentFilters] =
    useState<FiltersToApplyOnCampaignsPage>(filtersToApply);

  const [selectedProgramTypes, setSelectedProgramTypes] = useState<string[]>([]);
  const [selectedCampaignObjectives, setSelectedCampaignObjectives] = useState<string[]>([]);

  const programType = selectedProgramTypes.length === 1 ? selectedProgramTypes[0] : "";
  const campaignObjective =
    selectedCampaignObjectives.length === 1 ? selectedCampaignObjectives[0] : "";

  // Filters data
  const { data: programTypes = [] } = useGetProgramTypesQuery();
  const { data: campaignObjectiveTypes = [] } = useGetCampaignObjectiveTypesQuery(programType);

  const { data: audienceTypes = [] } = useGetAudienceTypesQuery();

  const filtersToDisplay = useMemo(() => {
    return CAMPAIGNS_TABLE_FILTERS_DATA.map((filter) => {
      let options = filter.options;

      switch (filter.filterId) {
        case "programType":
          options = programTypes;
          break;
        case "campaignObjective":
          options = campaignObjectiveTypes;
          break;
        case "audience":
          options = audienceTypes;
          break;
      }

      return {
        ...filter,
        options: options.map((opt) => ({
          name: opt.name,
          description: opt.description,
        })),
      };
    });
  }, [programTypes, campaignObjectiveTypes, audienceTypes]);

  const updateDependentFilters = (selectedFilter: string[], option: { name: string }) => {
    const currentValues = [...selectedFilter];
    const isSelected = currentValues.includes(option.name);

    if (isSelected) {
      // removing filter from the list if it is already selected
      currentValues.splice(currentValues.indexOf(option.name), 1);
    } else {
      // adding the filter to the list if it is not selected
      currentValues.unshift(option.name);
    }

    return currentValues;
  };

  const handleFilterChange = (filter: { filterId: string }, option: { name: string }) => {
    const filterKey = filter.filterId as keyof typeof currentFilters;
    const currentValues = (currentFilters[filterKey] as string[]) || [];

    const isSelected = currentValues.includes(option.name);

    const updatedCurrentFilters = {
      ...currentFilters,
      [filterKey]: isSelected
        ? currentValues.filter((item) => item !== option.name)
        : [...currentValues, option.name],
    };

    // Detecting program type to update the campaign objective types
    if (filter.filterId === "programType" && option.name) {
      const programTypeCurrentValues = updateDependentFilters(selectedProgramTypes, option);

      setSelectedProgramTypes(programTypeCurrentValues);
      // Clearing the campaign objective types
      updatedCurrentFilters.campaignObjective = [];
    }

    // Detecting campaign objective to update the class types
    if (filter.filterId === "campaignObjective" && option.name) {
      const campaignObjectiveCurrentValues = updateDependentFilters(
        selectedCampaignObjectives,
        option,
      );

      setSelectedCampaignObjectives(campaignObjectiveCurrentValues);
    }

    setCurrentFilters(updatedCurrentFilters);
    setFiltersToApply(updatedCurrentFilters);
  };

  // Sync currentFilters with filtersToApply when it changes externally
  useEffect(() => {
    setCurrentFilters(filtersToApply);
  }, [filtersToApply]);

  return {
    filtersToDisplay,
    selectedProgramTypes,
    selectedCampaignObjectives,
    handleFilterChange,
    currentFilters,
  };
};
